import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useGetPortalOrdersQuery } from "../../../store/apiSlice/portalOrderApiSlice";

export const usePortalOrders = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [activeFilters, setActiveFilters] = useState({});
  const [ordering, setOrdering] = useState("");

  const [searchValue, setSearchValueState] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const timerRef = useRef(null);

  const setSearchValue = useCallback(val => {
    setSearchValueState(val);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (val === "" || val.length >= 2) {
        setDebouncedSearch(val);
        setCurrentPage(1);
      }
    }, 400);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const offset = (currentPage - 1) * pageSize;

  // Convert activeFilters object into API parameters (comma-separated strings or date ranges)
  const filterParams = useMemo(() => {
    const params = {};
    Object.keys(activeFilters).forEach(key => {
      const val = activeFilters[key];
      if (Array.isArray(val) && val.length > 0) {
        params[key] = val.join(",");
      } else if (val && typeof val === "object" && !Array.isArray(val)) {
        if (val.start) params[`${key}_start`] = val.start;
        if (val.end) params[`${key}_end`] = val.end;
      }
    });
    return params;
  }, [activeFilters]);

  const { data, isLoading, isFetching } = useGetPortalOrdersQuery({
    limit: pageSize,
    offset,
    ordering: ordering || undefined,
    search: debouncedSearch || undefined,
    ...filterParams,
  });

  const orders = data?.results || [];
  const totalCount = data?.count || 0;

  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(size !== pageSize ? 1 : page);
    setPageSize(size);
  }, [pageSize]);

  const handleFilterApply = useCallback((key, values) => {
    setActiveFilters(prev => ({ ...prev, [key]: values }));
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((newOrdering) => {
    setOrdering(newOrdering);
    setCurrentPage(1);
  }, []);

  return {
    orders,
    totalCount,
    isLoading,
    isFetching,
    currentPage,
    pageSize,
    handlePageChange,
    activeFilters,
    handleFilterApply,
    ordering,
    handleSortChange,
    searchValue,
    setSearchValue,
  };
};
